// Factor and Remainder Theorem Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const factorRemainderSamples = {
  fundamental: [
    {
      id: 'factor_fund_01',
      problem: "Find the remainder when P(x) = x² + 3x + 2 is divided by (x-1)",
      problemType: "factor_remainder",
      difficulty: "fundamental",
      description: "Simple polynomial with linear divisor",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A mathematical expression with multiple terms",
            value: "P(x) = x² + 3x + 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "remainder_formula",
            name: "Remainder Formula",
            type: "formulas",
            description: "The mathematical formula for remainder theorem",
            value: "R = P(a) where (x-a) is the divisor",
            importance: 10,
            x: 0, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x-1)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = 1",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x-1)",
            value: "Remainder = 6",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "identify_divisor",
            name: "Identify Divisor",
            type: "steps",
            description: "Extract the divisor from the problem",
            value: "Step 1: Divisor is (x-1)",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "find_substitution",
            name: "Find Substitution Value",
            type: "steps",
            description: "Find the value to substitute (x-1 = 0)",
            value: "Step 2: x-1 = 0, so x = 1",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "substitute_and_calculate",
            name: "Substitute and Calculate",
            type: "steps",
            description: "Substitute x=1 into P(x) and calculate",
            value: "Step 3: P(1) = 1² + 3(1) + 2",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_result",
            name: "Final Result",
            type: "steps",
            description: "Calculate the final remainder",
            value: "Step 4: P(1) = 1 + 3 + 2 = 6",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "remainder_theorem", to: "remainder_formula", type: "expressed as", strength: 10 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 },
          { from: "polynomial", to: "identify_divisor", type: "requires", strength: 8 },
          { from: "identify_divisor", to: "find_substitution", type: "enables", strength: 9 },
          { from: "find_substitution", to: "substitute_and_calculate", type: "enables", strength: 10 },
          { from: "substitute_and_calculate", to: "final_result", type: "leads to", strength: 10 },
          { from: "final_result", to: "remainder_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'factor_fund_02',
      problem: "Find the remainder when P(x) = x³ - 2x² + x - 1 is divided by (x-2)",
      problemType: "factor_remainder",
      difficulty: "fundamental",
      description: "Cubic polynomial with simple substitution",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A cubic polynomial",
            value: "P(x) = x³ - 2x² + x - 1",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "remainder_formula",
            name: "Remainder Formula",
            type: "formulas",
            description: "The mathematical formula for remainder theorem",
            value: "R = P(a) where (x-a) is the divisor",
            importance: 10,
            x: 0, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x-2)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = 2",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x-2)",
            value: "Remainder = 1",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "remainder_theorem_formula",
            name: "Remainder Theorem Formula",
            type: "formulas",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "R = P(a) where (x-a) is the divisor",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_divisor",
            name: "Identify Divisor",
            type: "steps",
            description: "Extract the divisor from the problem",
            value: "Step 1: Divisor is (x-2)",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "find_substitution",
            name: "Find Substitution Value",
            type: "steps",
            description: "Find the value to substitute (x-2 = 0)",
            value: "Step 2: x-2 = 0, so x = 2",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "substitute_and_calculate",
            name: "Substitute and Calculate",
            type: "steps",
            description: "Substitute x=2 into P(x) and calculate",
            value: "Step 3: P(2) = 8 - 8 + 2 - 1",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "final_result",
            name: "Final Result",
            type: "steps",
            description: "Calculate the final remainder",
            value: "Step 4: P(2) = 8 - 8 + 2 - 1 = 1",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_divisor",
            name: "Identify Divisor",
            type: "steps",
            description: "Extract the divisor from the problem",
            value: "Step 1: Divisor is (x-2)",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "find_substitution",
            name: "Find Substitution Value",
            type: "steps",
            description: "Find the value to substitute (x-2 = 0)",
            value: "Step 2: x-2 = 0, so x = 2",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "substitute_and_calculate",
            name: "Substitute and Calculate",
            type: "steps",
            description: "Substitute x=2 into P(x) and calculate",
            value: "Step 3: P(2) = 8 - 8 + 2 - 1",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_result",
            name: "Final Result",
            type: "steps",
            description: "Calculate the final remainder",
            value: "Step 4: P(2) = 8 - 8 + 2 - 1 = 1",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "remainder_theorem", to: "remainder_formula", type: "expressed as", strength: 10 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 },
          { from: "polynomial", to: "identify_divisor", type: "requires", strength: 8 },
          { from: "identify_divisor", to: "find_substitution", type: "enables", strength: 9 },
          { from: "find_substitution", to: "substitute_and_calculate", type: "enables", strength: 10 },
          { from: "substitute_and_calculate", to: "final_result", type: "leads to", strength: 10 },
          { from: "final_result", to: "remainder_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'factor_fund_03',
      problem: "Find the remainder when P(x) = x² - 4 is divided by (x+2)",
      problemType: "factor_remainder",
      difficulty: "fundamental",
      description: "Quadratic with negative root",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A quadratic polynomial",
            value: "P(x) = x² - 4",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "remainder_formula",
            name: "Remainder Formula",
            type: "formulas",
            description: "The mathematical formula for remainder theorem",
            value: "R = P(a) where (x-a) is the divisor",
            importance: 10,
            x: 0, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x+2)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = -2",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x+2)",
            value: "Remainder = 0",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "identify_divisor",
            name: "Identify Divisor",
            type: "steps",
            description: "Extract the divisor from the problem",
            value: "Step 1: Divisor is (x+2)",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "find_substitution",
            name: "Find Substitution Value",
            type: "steps",
            description: "Find the value to substitute (x+2 = 0)",
            value: "Step 2: x+2 = 0, so x = -2",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "substitute_and_calculate",
            name: "Substitute and Calculate",
            type: "steps",
            description: "Substitute x=-2 into P(x) and calculate",
            value: "Step 3: P(-2) = (-2)² - 4 = 4 - 4",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "final_result",
            name: "Final Result",
            type: "steps",
            description: "Calculate the final remainder",
            value: "Step 4: P(-2) = 4 - 4 = 0",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "remainder_theorem", to: "remainder_formula", type: "expressed as", strength: 10 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 },
          { from: "polynomial", to: "identify_divisor", type: "requires", strength: 8 },
          { from: "identify_divisor", to: "find_substitution", type: "enables", strength: 9 },
          { from: "find_substitution", to: "substitute_and_calculate", type: "enables", strength: 10 },
          { from: "substitute_and_calculate", to: "final_result", type: "leads to", strength: 10 },
          { from: "final_result", to: "remainder_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'factor_fund_04',
      problem: "Find the remainder when P(x) = x³ + 1 is divided by (x+1)",
      problemType: "factor_remainder",
      difficulty: "fundamental",
      description: "Cubic with sum of cubes pattern",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A cubic polynomial",
            value: "P(x) = x³ + 1",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x+1)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = -1",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x+1)",
            value: "Remainder = 0",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'factor_fund_05',
      problem: "Find the remainder when P(x) = x² + 5x + 6 is divided by (x+3)",
      problemType: "factor_remainder",
      difficulty: "fundamental",
      description: "Quadratic with simple factoring",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A quadratic polynomial",
            value: "P(x) = x² + 5x + 6",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x+3)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = -3",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x+3)",
            value: "Remainder = 0",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'factor_inter_01',
      problem: "Find the remainder when P(x) = 2x³ - 3x² + 4x - 5 is divided by (x-2)",
      problemType: "factor_remainder",
      difficulty: "intermediate",
      description: "Cubic with leading coefficient ≠ 1",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A cubic polynomial with leading coefficient ≠ 1",
            value: "P(x) = 2x³ - 3x² + 4x - 5",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x-2)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = 2",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x-2)",
            value: "Remainder = 7",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'factor_inter_02',
      problem: "Find the remainder when P(x) = x⁴ - 3x² + 2x - 1 is divided by (x-1)",
      problemType: "factor_remainder",
      difficulty: "intermediate",
      description: "Quartic polynomial",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A quartic polynomial",
            value: "P(x) = x⁴ - 3x² + 2x - 1",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x-1)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = 1",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x-1)",
            value: "Remainder = -1",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'factor_inter_03',
      problem: "Find the remainder when P(x) = 3x³ + 2x² - 5x + 1 is divided by (x+1)",
      problemType: "factor_remainder",
      difficulty: "intermediate",
      description: "Cubic with negative root",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A cubic polynomial",
            value: "P(x) = 3x³ + 2x² - 5x + 1",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x+1)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = -1",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x+1)",
            value: "Remainder = 5",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'factor_inter_04',
      problem: "Find the remainder when P(x) = x³ - 6x² + 11x - 6 is divided by (x-3)",
      problemType: "factor_remainder",
      difficulty: "intermediate",
      description: "Cubic with integer root",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A cubic polynomial",
            value: "P(x) = x³ - 6x² + 11x - 6",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x-3)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = 3",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x-3)",
            value: "Remainder = 0",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'factor_inter_05',
      problem: "Find the remainder when P(x) = 2x³ - 5x² + 3x + 2 is divided by (x-2)",
      problemType: "factor_remainder",
      difficulty: "intermediate",
      description: "Cubic with mixed coefficients",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A cubic polynomial with mixed coefficients",
            value: "P(x) = 2x³ - 5x² + 3x + 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x-2)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = 2",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x-2)",
            value: "Remainder = 4",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'factor_hard_01',
      problem: "Find the remainder when P(x) = 4x⁴ - 3x³ + 2x² - x + 5 is divided by (x-1)",
      problemType: "factor_remainder",
      difficulty: "harder",
      description: "Quartic with leading coefficient ≠ 1",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A quartic polynomial with leading coefficient ≠ 1",
            value: "P(x) = 4x⁴ - 3x³ + 2x² - x + 5",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x-1)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = 1",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x-1)",
            value: "Remainder = 7",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'factor_hard_02',
      problem: "Find the remainder when P(x) = x⁵ - 2x³ + x² - 3x + 1 is divided by (x+1)",
      problemType: "factor_remainder",
      difficulty: "harder",
      description: "Quintic polynomial",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A quintic polynomial",
            value: "P(x) = x⁵ - 2x³ + x² - 3x + 1",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x+1)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = -1",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x+1)",
            value: "Remainder = 2",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'factor_hard_03',
      problem: "Find the remainder when P(x) = 3x⁴ - 4x³ + 2x² - 5x + 3 is divided by (x-2)",
      problemType: "factor_remainder",
      difficulty: "harder",
      description: "Quartic with complex coefficients",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A quartic polynomial with complex coefficients",
            value: "P(x) = 3x⁴ - 4x³ + 2x² - 5x + 3",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x-2)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = 2",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x-2)",
            value: "Remainder = 15",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'factor_hard_04',
      problem: "Find the remainder when P(x) = x⁴ - 5x³ + 6x² - 2x + 1 is divided by (x-3)",
      problemType: "factor_remainder",
      difficulty: "harder",
      description: "Quartic with larger coefficients",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A quartic polynomial with larger coefficients",
            value: "P(x) = x⁴ - 5x³ + 6x² - 2x + 1",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x-3)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = 3",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x-3)",
            value: "Remainder = 1",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'factor_hard_05',
      problem: "Find the remainder when P(x) = 2x⁵ - 3x⁴ + x³ - 4x² + 2x - 1 is divided by (x+2)",
      problemType: "factor_remainder",
      difficulty: "harder",
      description: "Quintic with negative root and leading coefficient ≠ 1",
      graphData: {
        nodes: [
          {
            id: "polynomial",
            name: "Polynomial",
            type: "concepts",
            description: "A quintic polynomial with leading coefficient ≠ 1",
            value: "P(x) = 2x⁵ - 3x⁴ + x³ - 4x² + 2x - 1",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "remainder_theorem",
            name: "Remainder Theorem",
            type: "concepts",
            description: "When P(x) is divided by (x-a), the remainder is P(a)",
            value: "Remainder = P(a)",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "divisor",
            name: "Divisor",
            type: "values",
            description: "The polynomial being divided by",
            value: "(x+2)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution_value",
            name: "Substitution Value",
            type: "values",
            description: "The value to substitute into the polynomial",
            value: "a = -2",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "remainder_result",
            name: "Remainder",
            type: "values",
            description: "The remainder when P(x) is divided by (x+2)",
            value: "Remainder = -89",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "polynomial", to: "remainder_theorem", type: "applies to", strength: 9 },
          { from: "divisor", to: "substitution_value", type: "determines", strength: 10 },
          { from: "remainder_theorem", to: "substitution_value", type: "uses", strength: 9 },
          { from: "substitution_value", to: "remainder_result", type: "gives", strength: 10 }
        ]
      }
    }
  ]
}; 